import Api from "services/Api";
import store from 'store/index';
import {previousValid} from 'store/generators/step';
import moment from "moment";
import _ from 'lodash';
import {requestPayload} from 'store/modules/claim';
import Validator from 'services/Validator';

export const mutationTypes = {
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER',
    UPDATE: 'UPDATE',
    RESET: 'RESET',
    RESET_IS_CONSTANT: 'RESET_IS_CONSTANT',
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'futureLosses': {type: 'text', rules: 'required'},
};

// Initial State Template
function initialState() {
    return {
        futureLosses: null,
        futureLossesConstant: null,
        futureLossesToRetirement: null,
        futureLossesStart: null,
        futureLossesEnd: null,
        futureLossesEndAge: null,
        preInjuryDiscountFactor: null,
        assessmentDiscountFactor: null,
        results: null,
        multiplier: 0,
        futureLossesMortalityMultiplier: false
    }
}

// Initial State
const state = initialState();

// Getters
const getters = {
    getLoss: state => key => state[key],
    shouldUpdatedFutureLosses: (state) => !!state.results
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     */
    save({getters, dispatch, rootState, state}, payload) {
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        }).then(() => dispatch('personal/save', requestPayload(0), {root: true}));
    },
    /**
     * Loads the step being received from the Server, to be placed in the state.
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;
        commit(mutationTypes.UPDATE_FROM_SERVER, {data});
    },
    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({commit, dispatch}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: true, // needs updating with real validity
        });
    },
    /**
     * Validate the step.
     *
     * @param commit
     * @param state
     * @param rootState
     */
    validate({commit, state, rootState}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: previousValid(rootState, 4)
                && (new Validator(state, rules)).validate(),
        });
    },
    /**
     * Resets the step back to defaults
     */
    reset({commit}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: false,
        });

        commit(mutationTypes.RESET);
        commit(mutationTypes.RESET_IS_CONSTANT);
    },

    /**
     * Reset is the future loss variable or constant
     * @param commit
     * @param rootState
     */
    resetIsConstant({commit, rootState}) {
        // update claimant details.
        rootState.personal.claimant.theClaimant = {
            ...rootState.personal.claimant.theClaimant,
            ...{
                levelOfEducation: null,
                employedAtAccident: null,
                employedAtTrial: null,
                disabledAtAccident: null,
                disabledAtTrial: null,
            }
        };

        commit(mutationTypes.RESET_IS_CONSTANT);
    },

    /**
     * Update Loss.
     */
    updateLoss({commit, state}, {key, value}) {
        commit(mutationTypes.UPDATE, {key, value});
    },
    /**
     * Update Discount Factors.
     *
     * This updates both the pre-injury and 'post-assessment' Table A–D
     * discount factors whenever education, disability or employment status
     * is changed by the user.
     *
     * This is also triggered whenever the claimant's date of birth or
     * gender changes.
     */
    updateDiscountFactors({commit, dispatch, state}) {
        let proms = ['preInjuryDiscountFactor', 'assessmentDiscountFactor']
            .map(key => dispatch('updateIndividualDiscountFactor', key));

        Promise
            .all(proms)
            .then(() => {
                // Update the page totals
            })
    },
    /**
     * Update a Discount Factor in Isolation.
     */
    updateIndividualDiscountFactor({commit, state, rootState, dispatch}, type) {
        // Claimant Store
        let claimant = rootState.personal.claimant;

        let education = claimant.theClaimant.levelOfEducation;
        let disabled = type === 'preInjuryDiscountFactor'
            ? claimant.theClaimant.disabledAtAccident
            : claimant.theClaimant.disabledAtTrial;
        let employment = type === 'preInjuryDiscountFactor'
            ? claimant.theClaimant.employedAtAccident
            : claimant.theClaimant.employedAtTrial;

        if (!education || employment === null || disabled === null) {
            return;
        }

        if (moment(claimant.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY')
            .diff(moment(claimant.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'years') > 54) {
            return commit(mutationTypes.UPDATE, {key: type, value: 1})
        }

        let payload = {
            age: moment(claimant.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY')
                .diff(moment(claimant.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'years'),
            gender: claimant.theClaimant.gender,
            education,
            disabled,
            employment: employment ? 'employed' : 'notEmployed',
        };

        return Api.calculators.monetary.employmentContingencyDiscount(payload)
            .then(data => commit(mutationTypes.UPDATE, {key: type, value: data.multiplier}))
            .then(() => dispatch('updateLossTotals'));
    },
    /**
     * Update Page Results.
     */
    updateResults({commit, state}) {
        if (
            !state.preInjuryDiscountFactor
            || !state.assessmentDiscountFactor
            || !state.futureLossesStart
            || !state.futureLossesEnd
        ) {
            return;
        }

        commit(mutationTypes.UPDATE, {key: 'results', value: [true]})
    },

    updateFutureLossOfEarnings: _.debounce(({state, commit, dispatch}) => {
        if (state.futureLossesStart && state.futureLossesEnd && state.futureLossesMortalityMultiplier !== null) {
            let claimInfo = store.getters['personal/claimant/getClaimInfo'];
            let claimantInfo = store.getters['personal/claimant/getClaimantValue'];
            return Api.calculators.monetary
                .futureLossMultiplier({
                    duration: moment(state.futureLossesEnd, 'DD/MM/YYYY').diff(moment(state.futureLossesStart, 'DD/MM/YYYY'), 'years', true),
                    discountRate: claimInfo('discountRate'),
                    gender: claimantInfo('gender'),
                    dateOfBirth: claimantInfo('dateOfBirth'),
                    assumedSettlement: claimInfo('dateOfAssumedSettlement'),
                    retirementAge: claimantInfo('income').retirementAge,
                    futureLossesToRetirement: state.futureLossesToRetirement
                })
                .then((data) => commit(mutationTypes.UPDATE, {key: 'multiplier', value: data.multiplier}))
                .then(() => dispatch('updateLossTotals'))
        }
    }, 500),

    updateLossTotals({state, dispatch, rootGetters}) {
        const totalNetIncome = rootGetters['personal/claimant/getIncomeValue']('futureEarningsPostAccident').totalNetIncome;
        if (!totalNetIncome || !state.multiplier || !state.assessmentDiscountFactor) {
            return;
        }
        let total = state.multiplier * state.assessmentDiscountFactor * totalNetIncome;
        return dispatch('personal/claimant/updateIncomeValue', {
            key: 'futureLossOfEarningsNetTotal',
            value: total
        }, {'root': true});
    }
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE](state, payload) {
        state[payload.key] = payload.value;
    },
    [mutationTypes.RESET](state) {
        state.futureLosses = null;
        state.futureLossesConstant = null;
        state.futureLossesToRetirement = null;
        state.futureLossesEnd = null;
        state.futureLossesEndAge = null;
        state.preInjuryDiscountFactor = null;
        state.assessmentDiscountFactor = null;
        state.results = null;
        state.multiplier = null;
        state.futureLossesMortalityMultiplier = false;
    },
    [mutationTypes.RESET_IS_CONSTANT](state, rootState) {
        state.preInjuryDiscountFactor = 0;
        state.assessmentDiscountFactor = 0;
        state.results = null;
    },
    [mutationTypes.UPDATE_FROM_SERVER](state, {data}) {
        state.futureLosses = data.futureLosses;
        state.futureLossesConstant = data.futureLossesConstant;
        state.futureLossesToRetirement = data.futureLossesToRetirement;
        state.futureLossesStart = data.futureLossesStart;
        state.futureLossesEnd = data.futureLossesEnd;
        state.futureLossesEndAge = data.futureLossesEndAge;
        state.preInjuryDiscountFactor = data.preInjuryDiscountFactor;
        state.assessmentDiscountFactor = data.assessmentDiscountFactor;
        state.results = data.results;
        state.multiplier = data.multiplier;
        state.futureLossesMortalityMultiplier = false;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}




// WEBPACK FOOTER //
// ./src/store/modules/personal/futureLosses.js